/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Search,
  Download,
  Clock,
  UserCheck,
  Trash2
} from "lucide-react";
import { databases } from "./appwrite"; 
import { Query } from "appwrite";

const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID || "regdb";
const ATT_COL = process.env.REACT_APP_APPWRITE_ATT_COL_ID || "regdb"; 

const TOTAL_PROGRAM_SESSIONS = 20;

export default function AttendanceDashboard({ onBack }) {
  const [uniqueParticipants, setUniqueParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [stats, setStats] = useState({
    total: 0,
    session1: 0,
    session2: 0,
    session3: 0,
    session4: 0,
  });

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const res = await databases.listDocuments(DB_ID, ATT_COL, [
        Query.limit(2000), 
        Query.orderDesc("$createdAt"),
      ]);

      processData(res.documents);
    } catch (err) {
      console.error("Error fetching attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  const processData = (data) => {
    const s = {
      total: data.length,
      session1: 0,
      session2: 0,
      session3: 0,
      session4: 0,
    };

    const grouped = {};

    data.forEach((log) => {
      // 1. Stats
      if (log.session === "1") s.session1++;
      if (log.session === "2") s.session2++;
      if (log.session === "3") s.session3++;
      if (log.session === "4") s.session4++;

      // 2. Grouping
      const email = log.email ? log.email.toLowerCase() : "unknown";
      
      if (!grouped[email]) {
        grouped[email] = {
          ...log,
          sessions: new Set([log.session]), 
          logIds: [log.$id],
          lastSeen: log.$createdAt
        };
      } else {
        grouped[email].sessions.add(log.session);
        grouped[email].logIds.push(log.$id);
        if (new Date(log.$createdAt) > new Date(grouped[email].lastSeen)) {
            grouped[email].lastSeen = log.$createdAt;
        }
      }
    });

    setStats(s);
    setUniqueParticipants(Object.values(grouped));
  };

  const handleDelete = async (participant) => {
    const confirmMsg = `⚠️ Are you sure you want to delete attendance for ${participant.name}?\n\nThis will remove ${participant.logIds.length} scanned record(s).`;
    if (!window.confirm(confirmMsg)) return;

    try {
        await Promise.all(
            participant.logIds.map(id => databases.deleteDocument(DB_ID, ATT_COL, id))
        );
        fetchAttendance();
    } catch (err) {
        alert("Error deleting records: " + err.message);
    }
  };

  const handleExport = () => {
    if (uniqueParticipants.length === 0) return alert("No data to export");

    const headers = [
      "Name", "Email", "Designation", "Total Sessions", "Sessions List", "Last Seen"
    ];
    
    const rows = uniqueParticipants.map((p) => {
      const sessionList = Array.from(p.sessions).sort().join(" & ");
      return [
        `"${p.name}"`, `"${p.email}"`, `"${p.designation || "-"}"`,
        p.sessions.size, `"${sessionList}"`, new Date(p.lastSeen).toLocaleString()
      ].join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance_summary_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filtered = uniqueParticipants.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-screen bg-gray-50">
      <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 animate-fadeIn">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Attendance Summary
              </h2>
              <p className="text-sm text-gray-500">
                Consolidated view of {uniqueParticipants.length} participants
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAttendance}
              className="bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 font-medium"
            >
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" /> Export Summary
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gray-800 text-white p-4 rounded-2xl">
            <p className="text-xs opacity-70 uppercase tracking-wider">Total Scans</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          {['1','2','3','4'].map(num => (
             <div key={num} className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <p className="text-xs text-gray-500 uppercase font-bold">Session {num}</p>
                <p className="text-xl font-bold text-blue-600">{stats[`session${num}`]}</p>
             </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search participants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider font-semibold">
                <th className="p-4">Participant</th>
                <th className="p-4">Sessions Attended</th>
                <th className="p-4">Last Active</th>
                <th className="p-4">Designation</th>
                <th className="p-4 w-48">Cumulative Attendance</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">Loading records...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500 italic">No participants found.</td></tr>
              ) : (
                filtered.map((r) => {
                  const count = r.sessions.size;
                  const percentage = Math.min(100, (count / TOTAL_PROGRAM_SESSIONS) * 100);
                  const progressColor = percentage < 50 ? "bg-red-500" : percentage < 75 ? "bg-yellow-500" : "bg-green-500";
                  const sessionBadges = Array.from(r.sessions).sort();

                  return (
                    <tr key={r.email} className="hover:bg-gray-50 transition-colors group">
                      <td className="p-4">
                        <div className="font-bold text-gray-900 flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-green-500" />
                          {r.name.toUpperCase()}
                        </div>
                        <div className="text-xs text-gray-500 ml-6">{r.email}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                            {sessionBadges.map(s => (
                                <span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100 font-mono">S{s}</span>
                            ))}
                        </div>
                      </td>
                      <td className="p-4 text-gray-600 font-mono text-xs">
                        <div className="flex items-center gap-2 bg-gray-50 w-fit px-2 py-1 rounded">
                          <Clock className="w-3 h-3 text-gray-400" />
                          {new Date(r.lastSeen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          <span className="text-gray-400">|</span>
                          {new Date(r.lastSeen).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-4 text-gray-500 text-xs">{r.designation || "N/A"}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                            <div className="flex justify-between text-xs font-semibold text-gray-600">
                                <span>{count} / {TOTAL_PROGRAM_SESSIONS}</span>
                                <span>{Math.round(percentage)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div className={`h-2.5 rounded-full ${progressColor} transition-all duration-500`} style={{ width: `${percentage}%` }}></div>
                            </div>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <button onClick={() => handleDelete(r)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete all attendance records for this user">
                            <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}